import {
  FileItem,
  FileRef,
  FolderItem,
  FolderVisibility,
  TemplateFolder,
} from "./files.types";

// Same rules as the backend (DocumentUploads.cs).
export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_FILES = 20;
export const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".heic",
  ".heif",
  ".webp",
  ".tif",
  ".tiff",
  ".bmp",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".xlsm",
  ".csv",
  ".txt",
  ".rtf",
  ".ppt",
  ".pptx",
  ".odt",
  ".ods",
  ".zip",
  ".msg",
  ".eml",
  ".qbb",
  ".qbm",
  ".ofx",
  ".qfx",
];
export const ACCEPT = ALLOWED_EXTENSIONS.join(",");

export const extensionOf = (name: string) => {
  const dot = (name || "").lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot).toLowerCase();
};

/** Why these files can't be uploaded, or null when they're fine. */
export const uploadProblem = (files: { name: string; size: number }[]) => {
  if (files.length === 0) return "Choose at least one file.";
  if (files.length > MAX_FILES)
    return `Upload at most ${MAX_FILES} files at a time.`;
  for (const file of files) {
    if (file.size === 0) return `"${file.name}" is empty.`;
    if (file.size > MAX_FILE_BYTES)
      return `"${file.name}" is larger than 50 MB.`;
    if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
      return `"${file.name}" can't be uploaded. Use PDF, photos, Word, Excel, CSV or ZIP files.`;
    }
  }
  return null;
};

export type FileType = "pdf" | "word" | "excel" | "image" | "other";

export const fileTypeOf = (name: string): FileType => {
  const ext = extensionOf(name);
  if (ext === ".pdf") return "pdf";
  if ([".doc", ".docx", ".rtf", ".odt", ".txt"].includes(ext)) return "word";
  if ([".xls", ".xlsx", ".xlsm", ".csv", ".ods"].includes(ext)) return "excel";
  if (
    [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".heic",
      ".heif",
      ".webp",
      ".tif",
      ".tiff",
      ".bmp",
    ].includes(ext)
  ) {
    return "image";
  }
  return "other";
};

/** Short label and color for the file icon. */
export const fileBadge = (name: string) => {
  const type = fileTypeOf(name);
  const ext = extensionOf(name).replace(".", "").toUpperCase().slice(0, 4);
  const colors: Record<FileType, string> = {
    pdf: "#E5484D",
    word: "#2F6FEB",
    excel: "#16A34A",
    image: "#D97706",
    other: "#7C8396",
  };
  return { label: ext || "FILE", color: colors[type] };
};

export const formatSize = (bytes?: number | null) => {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const visibilityLabel = (visibility: FolderVisibility) => {
  switch (visibility) {
    case "shared":
      return "Client can see";
    case "uploads":
      return "Client can see and add files";
    case "inherit":
      return "Same as the folder above";
    default:
      return "Firm only";
  }
};

export const toRef = (file: Pick<FileItem, "kind" | "id">): FileRef => ({
  kind: file.kind,
  id: file.id,
});

export const sameRef = (a: FileRef, b: FileRef) =>
  a.kind === b.kind && a.id === b.id;

export const refKey = (ref: FileRef) => `${ref.kind}:${ref.id}`;

// ---------- Folders ----------

export type FolderNode = FolderItem & { children: FolderNode[] };

const byOrder = (a: FolderItem, b: FolderItem) =>
  a.sortOrder - b.sortOrder ||
  a.name.localeCompare(b.name, undefined, { numeric: true });

/** Folders as a tree (top-level first, in their order). Broken parent links count as top level. */
export const buildTree = (folders: FolderItem[]): FolderNode[] => {
  const ids = new Set(folders.map((f) => f.id));
  const make = (parentId: string | null, seen: Set<string>): FolderNode[] =>
    folders
      .filter((f) =>
        parentId === null
          ? !f.parentId || !ids.has(f.parentId)
          : f.parentId === parentId,
      )
      .filter((f) => !seen.has(f.id))
      .sort(byOrder)
      .map((f) => {
        const next = new Set(seen).add(f.id);
        return { ...f, children: make(f.id, next) };
      });
  return make(null, new Set());
};

/** The folder and its parents, top-level first. */
export const folderChain = (folders: FolderItem[], folderId: string | null) => {
  const map = new Map(folders.map((f) => [f.id, f]));
  const chain: FolderItem[] = [];
  const seen = new Set<string>();
  let current = folderId ? map.get(folderId) : undefined;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    chain.unshift(current);
    current = current.parentId ? map.get(current.parentId) : undefined;
  }
  return chain;
};

/** The folder and every folder inside it. */
export const folderSubtree = (folders: FolderItem[], folderId: string) => {
  const result = new Set([folderId]);
  let added = true;
  while (added) {
    added = false;
    folders.forEach((f) => {
      if (f.parentId && result.has(f.parentId) && !result.has(f.id)) {
        result.add(f.id);
        added = true;
      }
    });
  }
  return result;
};

/** Where a folder may be moved: anywhere except into itself or its own subfolders. */
export const canMoveFolder = (
  folders: FolderItem[],
  folderId: string,
  targetId: string | null,
) => targetId === null || !folderSubtree(folders, folderId).has(targetId);

// ---------- Which files a view shows ----------

export type FilesView =
  | { type: "top" }
  | { type: "folder"; id: string }
  | { type: "toSort" }
  | { type: "work"; id: string };

export const isToSort = (file: FileItem) =>
  file.kind === "client" && file.fromClient && !file.folderId;

export const filesInView = (files: FileItem[], view: FilesView) => {
  switch (view.type) {
    case "folder":
      return files.filter((f) => f.kind === "client" && f.folderId === view.id);
    case "toSort":
      return files.filter(isToSort);
    case "work":
      return files.filter((f) => f.workId === view.id);
    default:
      return files.filter(
        (f) => f.kind === "client" && !f.folderId && !isToSort(f),
      );
  }
};

export type FileFilters = {
  search?: string;
  addedBy?: "" | "firm" | "client" | "me";
  type?: "" | FileType;
  clientCanSee?: "" | "yes" | "no";
  me?: string;
};

export const hasFilters = (filters: FileFilters) =>
  !!(
    filters.search?.trim() ||
    filters.addedBy ||
    filters.type ||
    filters.clientCanSee
  );

/** Search and filter pills; search also matches the folder path and work item. */
export const filterFiles = (files: FileItem[], filters: FileFilters) => {
  const search = (filters.search || "").trim().toLowerCase();
  return files.filter((f) => {
    if (search) {
      const haystack = [
        f.name,
        f.folderPath,
        f.workName,
        f.addedBy,
        f.customerName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    if (filters.addedBy === "client" && !f.fromClient) return false;
    if (filters.addedBy === "firm" && f.fromClient) return false;
    if (filters.addedBy === "me" && (!filters.me || f.addedBy !== filters.me))
      return false;
    if (filters.type && fileTypeOf(f.name) !== filters.type) return false;
    if (filters.clientCanSee === "yes" && !f.clientCanSee) return false;
    if (filters.clientCanSee === "no" && f.clientCanSee) return false;
    return true;
  });
};

export type FileSort = "newest" | "oldest" | "name" | "size";

export const sortFiles = (files: FileItem[], sort: FileSort) => {
  const list = [...files];
  const time = (f: FileItem) => (f.addedAt ? new Date(f.addedAt).getTime() : 0);
  switch (sort) {
    case "oldest":
      return list.sort((a, b) => time(a) - time(b));
    case "name":
      return list.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      );
    case "size":
      return list.sort((a, b) => (b.size || 0) - (a.size || 0));
    default:
      return list.sort((a, b) => time(b) - time(a));
  }
};

/** Number of files directly in a folder and in all its subfolders. */
export const countInFolder = (
  folders: FolderItem[],
  files: FileItem[],
  folderId: string,
) => {
  const subtree = folderSubtree(folders, folderId);
  return files.filter(
    (f) => f.kind === "client" && f.folderId && subtree.has(f.folderId),
  ).length;
};

/** Files that can be shared one by one (client files the firm added). */
export const canShareDirectly = (file: FileItem) =>
  file.kind === "client" && !file.fromClient;

// ---------- Folder templates ----------

export const THIS_YEAR = "{this year}";

export const expandYear = (name: string, year: number) =>
  name.replace(/\{this year\}/gi, String(year));

/** A path to a template folder: indexes from the top. */
export type TemplatePath = number[];

const cloneTemplate = (folders: TemplateFolder[]): TemplateFolder[] =>
  folders.map((f) => ({ ...f, children: cloneTemplate(f.children || []) }));

const levelAt = (folders: TemplateFolder[], parent: TemplatePath) => {
  let level = folders;
  parent.forEach((i) => {
    level = level[i].children;
  });
  return level;
};

export const addTemplateFolder = (
  folders: TemplateFolder[],
  parent: TemplatePath,
  name: string,
) => {
  const copy = cloneTemplate(folders);
  levelAt(copy, parent).push({
    name,
    visibility: parent.length === 0 ? "private" : "inherit",
    children: [],
  });
  return copy;
};

export const updateTemplateFolder = (
  folders: TemplateFolder[],
  path: TemplatePath,
  change: Partial<Pick<TemplateFolder, "name" | "visibility">>,
) => {
  const copy = cloneTemplate(folders);
  const level = levelAt(copy, path.slice(0, -1));
  const index = path[path.length - 1];
  level[index] = { ...level[index], ...change };
  return copy;
};

export const removeTemplateFolder = (
  folders: TemplateFolder[],
  path: TemplatePath,
) => {
  const copy = cloneTemplate(folders);
  levelAt(copy, path.slice(0, -1)).splice(path[path.length - 1], 1);
  return copy;
};

export const moveTemplateFolder = (
  folders: TemplateFolder[],
  path: TemplatePath,
  delta: -1 | 1,
) => {
  const copy = cloneTemplate(folders);
  const level = levelAt(copy, path.slice(0, -1));
  const index = path[path.length - 1];
  const target = index + delta;
  if (target < 0 || target >= level.length) return copy;
  [level[index], level[target]] = [level[target], level[index]];
  return copy;
};

/** Why a template can't be saved, or null. Mirrors the backend rules. */
export const templateProblem = (name: string, folders: TemplateFolder[]) => {
  if (!name.trim()) return "Enter a template name.";
  if (folders.length === 0) return "Add at least one folder.";
  let count = 0;
  const check = (level: TemplateFolder[], depth: number): string | null => {
    if (depth > 6) return "Folders can be at most 6 levels deep.";
    const names = new Set<string>();
    for (const folder of level) {
      const clean = folder.name.trim();
      if (!clean) return "Every folder needs a name.";
      if (/[\\/]/.test(clean)) return "A folder name can't contain / or \\.";
      if (names.has(clean.toLowerCase()))
        return `"${clean}" appears twice in the same folder.`;
      names.add(clean.toLowerCase());
      count += 1;
      const inner = check(folder.children || [], depth + 1);
      if (inner) return inner;
    }
    return null;
  };
  const problem = check(folders, 1);
  if (problem) return problem;
  if (count > 100) return "A template can have at most 100 folders.";
  return null;
};

export const countTemplateFolders = (folders: TemplateFolder[]): number =>
  folders.reduce(
    (sum, f) => sum + 1 + countTemplateFolders(f.children || []),
    0,
  );
